import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { ChevronDown } from "lucide-react"
import * as React from "react"

export interface ProductGroupOption {
  value: string
  count: number
}

interface ArticleProductGroupComboboxProps {
  /** Controlled value — flows straight through onChange on every keystroke,
   * exactly like a plain <Input> (empty string included). Picking a
   * suggestion is just a shortcut that fills the same value in. */
  value: string
  onChange: (value: string) => void
  /** Existing group values with how many articles carry each one. */
  options: ProductGroupOption[]
  placeholder?: string
  /** "Create new {label}: {text}" for a value no existing group matches.
   *  Called with the typed text; omit to hide that entry. */
  createLabel?: (text: string) => string
  className?: string
  id?: string
  /** The component renders no visible label of its own, and a placeholder is
   *  not an accessible name (it vanishes as soon as the user types), so the
   *  caller names the field with one of these. */
  "aria-label"?: string
  "aria-labelledby"?: string
}

/**
 * Free-text field with an existing-values dropdown for the product group
 * (org label configurable via templateSettings.productGroupLabel — this
 * component itself is label-agnostic). Shared by the single-article field
 * (ArticleFieldsSection) and the bulk-assign section
 * (ArticleProductGroupAssignSection) so both offer the same pick-or-type
 * behavior from one implementation.
 *
 * Hand-rolled rather than cmdk: the text input has to keep focus for free-text
 * typing, which puts it outside a <Command> container, and cmdk only handles
 * arrow keys for the element that owns focus. One keydown handler here drives
 * both the typing and the list.
 */
export function ArticleProductGroupCombobox({
  value,
  onChange,
  options,
  placeholder,
  createLabel,
  className,
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: ArticleProductGroupComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(0)
  // Filtering follows what the user TYPES, not a value filled in for them (a
  // prefilled group, or one just picked). Otherwise opening a filled field
  // would list only its own group, with no way to switch without clearing it.
  const [typing, setTyping] = React.useState(false)
  const reactId = React.useId()
  const listId = `${id ?? reactId}-listbox`
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  const typed = value.trim()
  const filtered = React.useMemo(() => {
    const q = typing ? typed.toLowerCase() : ""
    return q
      ? options.filter((o) => o.value.toLowerCase().includes(q))
      : options
  }, [options, typed, typing])

  // Only while typing, and only when the text matches no existing group
  // exactly — otherwise the user is picking that group, not making a new one.
  const showCreate =
    createLabel != null &&
    typing &&
    typed.length > 0 &&
    !options.some((o) => o.value.toLowerCase() === typed.toLowerCase())

  // The create entry is the first row, so every index below counts from it.
  const rowCount = filtered.length + (showCreate ? 1 : 0)

  React.useEffect(() => setActiveIndex(0), [typed, open])

  // Click outside closes. Pointerdown, not click, so a pick on a row still
  // fires before the list unmounts.
  React.useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [open])

  function commit(index: number) {
    setTyping(false)
    if (showCreate && index === 0) {
      setOpen(false)
      return // the typed text is already the value
    }
    const option = filtered[showCreate ? index - 1 : index]
    if (!option) return
    onChange(option.value)
    setOpen(false)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const pressed = event.key
    if (pressed === "Escape") {
      setOpen(false)
      return
    }
    if (pressed === "ArrowDown" || pressed === "ArrowUp") {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      if (rowCount === 0) return
      setActiveIndex((i) =>
        pressed === "ArrowDown"
          ? (i + 1) % rowCount
          : (i - 1 + rowCount) % rowCount,
      )
      return
    }
    if (pressed === "Enter" && open && rowCount > 0) {
      event.preventDefault()
      commit(activeIndex)
    }
  }

  function rowId(index: number) {
    return `${listId}-row-${index}`
  }

  return (
    <div ref={wrapperRef} className="relative w-full">
      <Input
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && rowCount > 0 ? rowId(activeIndex) : undefined
        }
        aria-autocomplete="list"
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
          setTyping(true)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={cn("pr-8", className)}
        autoComplete="off"
      />
      <ChevronDown
        size={14}
        aria-hidden
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-grey5"
      />

      {open && rowCount > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-[220px] w-full overflow-y-auto rounded-md border border-grey4 bg-grey2 py-1 shadow-lg"
        >
          {showCreate && (
            <li
              id={rowId(0)}
              role="option"
              aria-selected={activeIndex === 0}
              onPointerDown={(event) => event.preventDefault()}
              onClick={() => commit(0)}
              className={cn(
                "cursor-pointer px-3 py-1.5 text-[13px] text-yellow1",
                activeIndex === 0 && "bg-grey3",
              )}
            >
              {createLabel(typed)}
            </li>
          )}
          {filtered.map((option, i) => {
            const index = showCreate ? i + 1 : i
            return (
              <li
                key={option.value}
                id={rowId(index)}
                role="option"
                aria-selected={activeIndex === index}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => commit(index)}
                className={cn(
                  "flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5 text-[13px] text-white",
                  activeIndex === index && "bg-grey3",
                )}
              >
                <span className="truncate">{option.value}</span>
                <span className="shrink-0 text-xs text-grey5">
                  {option.count}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
